import React, { Component, ReactNode } from 'react';
import { Box, Paper, Typography, Button, Alert, CircularProgress } from '@mui/material';
import { ErrorOutline as ErrorIcon } from '@mui/icons-material';
import logger from '../../utils/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  /**
   * チャンク取得失敗からリロードで回復しようとしている最中か (Issue #19)。
   * このときエラー画面を出すと、リロードで消える画面が一瞬ちらつく。
   */
  recovering: boolean;
}

/**
 * チャンク再取得のためのリロードを何回試したかを記録するキー (Issue #19)
 */
const CHUNK_RELOAD_KEY = 'conftrack:chunk-reload-attempts';

/**
 * リロードを試す上限。超えたらエラー画面を出し、あとは利用者の操作に委ねる。
 *
 * 「直近 N 秒以内なら諦める」という時間ベースの判定にすると、リロードから再失敗までが
 * その時間を超えた場合 (会場の混雑した回線では容易に起こる) に毎回「久しぶりの失敗」と
 * 判定され、リロードが止まらなくなる。回数で数えれば所要時間に関係なく必ず止まる。
 *
 * sessionStorage なのでタブを閉じれば 0 に戻る。1 つのタブを開いたまま複数回デプロイが
 * あっても回復できるよう、1 回ではなく 2 回までは試す。
 */
const MAX_CHUNK_RELOAD_ATTEMPTS = 2;

/**
 * これまでのリロード試行回数。sessionStorage が使えない場合は null。
 * 判定に副作用を持ち込まないよう、読み取りと記録を分けている。
 */
function readChunkReloadAttempts(): number | null {
  try {
    const raw = Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
    return Number.isFinite(raw) && raw >= 0 ? raw : 0;
  } catch {
    // プライベートブラウジング等。ループを検知する手段が無いので自動リロードはしない
    return null;
  }
}

/**
 * 自動リロードを試してよいか。副作用を持たないので render 系から呼んでよい。
 */
export function canAttemptChunkReload(): boolean {
  const attempts = readChunkReloadAttempts();
  return attempts !== null && attempts < MAX_CHUNK_RELOAD_ATTEMPTS;
}

/**
 * lazy import したチャンクの取得失敗かどうかを判定する。
 * webpack は ChunkLoadError という name を付けるが、ブラウザや発生経路によっては
 * name が落ちてメッセージだけになることがあるため両方見る。
 */
export function isChunkLoadError(error: Error): boolean {
  return (
    error.name === 'ChunkLoadError' ||
    /Loading (CSS )?chunk [^\s]+ failed/i.test(error.message ?? '')
  );
}

/**
 * Reactコンポーネントツリー内のエラーを捕捉するエラーバウンダリ
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      recovering: false,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // エラーが発生したら、次のレンダーでフォールバックUIを表示。
    // ただしリロードで回復できる見込みがあるなら、エラー画面は出さない (Issue #19)。
    // ここでは判定のみで、記録とリロードは componentDidCatch で行う。
    return {
      hasError: true,
      error,
      errorInfo: null,
      recovering: isChunkLoadError(error) && canAttemptChunkReload(),
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // 古いバンドルを掴んでいるだけなら、エラー画面を出さずにリロードで回復させる (Issue #19)。
    //
    // デプロイでバンドル構成が変わると、ブラウザに残った古い index.html が参照するチャンクは
    // S3 から削除済みで 404 になる。再読み込みすれば新しい index.html を取得できるので直るが、
    // 直らない場合 (本当にファイルが無い等) に無条件でリロードすると止まらなくなるため、
    // 試行回数を sessionStorage に記録して上限を設ける。
    if (this.state.recovering) {
      const attempts = readChunkReloadAttempts() ?? 0;
      try {
        window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(attempts + 1));
      } catch {
        // 直前の canAttemptChunkReload() が通っている以上ここには来ないが、
        // 記録できないままリロードすると上限が効かないので中断する
        logger.error('Failed to record the chunk reload attempt; skipping the automatic reload');
        this.setState({ recovering: false });
        return;
      }
      logger.info(`ChunkLoadError detected; reloading to fetch the latest bundle (attempt ${attempts + 1})`);
      window.location.reload();
      return;
    }

    // エラー情報をログに記録
    logger.error('ErrorBoundary caught an error:', error, errorInfo);

    // エラー情報を状態に保存
    this.setState({
      error,
      errorInfo,
    });

    // 親コンポーネントにエラーを通知
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // 本番環境では外部エラー追跡サービスに送信
    const environment = process.env.REACT_APP_ENVIRONMENT || 'production';
    if (environment === 'prd') {
      // 例: Sentry, LogRocket, Bugsnag などにエラーを送信
      this.logErrorToService(error, errorInfo);
    }
  }

  logErrorToService(error: Error, errorInfo: React.ErrorInfo) {
    // 外部エラー追跡サービスへの送信ロジック
    // TODO: 実際のサービス統合を実装
    logger.info('Error logged to external service:', {
      error: error.toString(),
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      recovering: false,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Issue #19: リロードで回復する見込みなら、消える前提のエラー画面を出さない。
      // getDerivedStateFromError は componentDidCatch より先に走るため、ここを通さないと
      // リロードが始まるまでの一瞬だけエラー画面がちらつく。
      if (this.state.recovering) {
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '100vh',
              bgcolor: 'background.default',
            }}
          >
            <CircularProgress />
          </Box>
        );
      }

      // カスタムフォールバックUIが提供されている場合
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      // デフォルトのエラーUI
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            bgcolor: 'background.default',
            p: 3,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              maxWidth: 600,
              width: '100%',
              p: 4,
              textAlign: 'center',
            }}
          >
            <ErrorIcon
              sx={{
                fontSize: 64,
                color: 'error.main',
                mb: 2,
              }}
            />
            
            <Typography variant="h4" gutterBottom>
              エラーが発生しました
            </Typography>
            
            <Typography variant="body1" color="text.secondary" paragraph>
              申し訳ございません。予期しないエラーが発生しました。
              問題が解決しない場合は、管理者にお問い合わせください。
            </Typography>

            {process.env.REACT_APP_ENVIRONMENT === 'dev' && this.state.error && (
              <Alert severity="error" sx={{ mt: 2, mb: 2, textAlign: 'left' }}>
                <Typography variant="subtitle2" gutterBottom>
                  <strong>エラー詳細（開発環境のみ表示）:</strong>
                </Typography>
                <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                  {this.state.error.toString()}
                </Typography>
                {this.state.errorInfo && (
                  <details style={{ marginTop: '8px' }}>
                    <summary style={{ cursor: 'pointer' }}>コンポーネントスタック</summary>
                    <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', mt: 1 }}>
                      {this.state.errorInfo.componentStack}
                    </Typography>
                  </details>
                )}
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
              <Button
                variant="contained"
                onClick={this.handleReset}
                color="primary"
              >
                再試行
              </Button>
              <Button
                variant="outlined"
                onClick={this.handleReload}
                color="secondary"
              >
                ページを再読み込み
              </Button>
            </Box>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}